var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { GridIcon, UserIcon } from '../icon';
import { styled } from '../styled';
import clsx from '../utils/clsx';
import { useAvatarContext } from './avatar-context';
import { useAvatarImage } from './use-avatar-image';
import { avatarComponentName, avatarClasses, getAvatarUtilityClass } from './avatar-classes';
import { useMergedClasses } from '../styles/classes';
var Root = styled('div', { name: avatarComponentName, slot: 'Root' })(function (_a) {
    var theme = _a.theme, ownerState = _a.ownerState;
    var fullTokens = theme.tokens;
    return __assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign(__assign({ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', height: theme.typography.pxToRem(40), width: theme.typography.pxToRem(40), color: theme.tokens.color_fg_on_state_neutral_subtle, backgroundColor: theme.tokens.color_bg_state_neutral_subtle, fontSize: theme.typography.pxToRem(16), fontWeight: theme.typography.fontWeightMedium, textTransform: 'uppercase', '& > svg': {
            height: theme.typography.pxToRem(24),
            width: theme.typography.pxToRem(24),
        } }, (ownerState.type === 'logo' && {
        backgroundColor: theme.mixins.getColorModeValue({
            light: 'transparent',
            dark: theme.unsafe_globalTokens.color_global_functional_static_neutral_white,
        }),
        // adding padding due to the addition of a background for some.
        padding: theme.palette.mode === 'dark' ? theme.spacing(0.25) : undefined,
    })), (ownerState.showBorder && {
        border: "1px solid ".concat(theme.tokens.color_border_bold),
        '&$logo$hasImg': {
            backgroundColor: theme.tokens.color_bg_layer,
        },
    })), (ownerState.includePadding &&
        ownerState.type === 'logo' && {
        padding: ownerState.size === 'small'
            ? theme.spacing(0.5)
            : ownerState.size === 'large'
                ? theme.spacing(1.5)
                : ownerState.size === 'xlarge'
                    ? theme.spacing(3)
                    : theme.spacing(0.75),
    })), (ownerState.placement === 'badge' && {
        border: "3px solid ".concat(theme.tokens.color_bg_layer),
    })), (ownerState.variant === 'rounded' && {
        borderRadius: 4,
    })), (ownerState.variant === 'circular' && {
        minWidth: 'unset',
        fontSize: theme.typography.button.fontSize,
        borderRadius: '50%',
    })), (ownerState.size === 'xsmall' && __assign({ height: theme.typography.pxToRem(24), width: theme.typography.pxToRem(24), fontSize: theme.typography.pxToRem(12), '& > svg': {
            height: theme.typography.pxToRem(16),
            width: theme.typography.pxToRem(16),
        } }, (ownerState.variant === 'rounded' && {
        borderRadius: 2,
    })))), (ownerState.size === 'small' && __assign({ height: theme.typography.pxToRem(32), width: theme.typography.pxToRem(32), fontSize: theme.typography.pxToRem(14), '& > svg': {
            height: theme.typography.pxToRem(16),
            width: theme.typography.pxToRem(16),
        } }, (ownerState.variant === 'rounded' && {
        borderRadius: ownerState.placement === 'badge' ? 8 : 2,
    })))), (ownerState.size === 'medium' && {
        fontSize: theme.typography.pxToRem(16),
    })), (ownerState.size === 'large' && __assign({ height: theme.typography.pxToRem(64), width: theme.typography.pxToRem(64), fontSize: theme.typography.pxToRem(24), '& > svg': {
            height: theme.typography.pxToRem(40),
            width: theme.typography.pxToRem(40),
        } }, (ownerState.variant === 'rounded' && {
        borderRadius: 6,
    })))), (ownerState.size === 'xlarge' && __assign({ height: theme.typography.pxToRem(128), width: theme.typography.pxToRem(128), fontSize: theme.typography.pxToRem(48), fontWeight: theme.typography.fontWeightRegular, '& > svg': {
            height: theme.typography.pxToRem(80),
            width: theme.typography.pxToRem(80),
        } }, (ownerState.variant === 'rounded' && {
        borderRadius: 8,
    })))), ((ownerState.color === 'primary' || ownerState.color === 'purple') && {
        backgroundColor: theme.tokens.color_bg_brand_primary_subtle,
        color: theme.tokens.color_fg_on_brand_primary_subtle,
    })), ((ownerState.color === 'info' || ownerState.color === 'blue') && {
        backgroundColor: theme.tokens.color_bg_state_info_subtle,
        color: theme.tokens.color_fg_on_state_info_subtle,
    })), ((ownerState.color === 'warning' || ownerState.color === 'yellow') && {
        backgroundColor: theme.tokens.color_bg_state_caution_subtle,
        color: theme.tokens.color_fg_on_state_caution_subtle,
    })), ((ownerState.color === 'danger' || ownerState.color === 'red') && {
        backgroundColor: theme.tokens.color_bg_state_danger_subtle,
        color: theme.tokens.color_fg_on_state_danger_subtle,
    })), ((ownerState.color === 'success' || ownerState.color === 'green') && {
        backgroundColor: theme.tokens.color_bg_state_success_subtle,
        color: theme.tokens.color_fg_on_state_success_subtle,
    })), (ownerState.color === 'magenta' && {
        backgroundColor: theme.mixins.getColorModeValue({
            dark: fullTokens.color_global_functional_dark_magenta_200,
            light: fullTokens.color_global_functional_light_magenta_200,
        }),
        color: theme.mixins.getColorModeValue({
            dark: fullTokens.color_global_functional_dark_magenta_800,
            light: fullTokens.color_global_functional_light_magenta_800,
        }),
    }));
});
var Img = styled('img', { name: avatarComponentName, slot: 'Img' })(function (_a) {
    var ownerState = _a.ownerState;
    return ({
        width: '100%',
        height: '100%',
        textAlign: 'center',
        // Handle non-square image. The property isn't supported by IE 11.
        objectFit: ownerState.imgFit === 'contain' ? 'contain' : 'cover',
        // Hide alt text.
        color: 'transparent',
        // Hide the image broken icon, only works on Chrome.
        textIndent: 10000,
    });
});
var getDefaultVariantForType = function (type) {
    switch (type) {
        case 'logo':
            return 'square';
        case 'resource':
            return 'rounded';
        case 'user':
            return 'circular';
    }
};
var fallbackIcon = {
    resource: GridIcon,
    user: UserIcon,
    logo: GridIcon,
};
export var Avatar = React.forwardRef(function (props, ref) {
    var className = props.className, alt = props.alt, color = props.color, childrenProp = props.children, imgProps = props.imgProps, includePadding = props.includePadding, showBorder = props.showBorder, sizeProp = props.size, _a = props.imgFit, imgFit = _a === void 0 ? 'cover' : _a, srcProp = props.src, fallbackIconOverride = props.fallbackIconOverride, typeProp = props.type, variantProp = props.variant, email = props.email, initials = props.initials, _b = props.placement, placement = _b === void 0 ? 'default' : _b, rootProps = __rest(props, ["className", "alt", "color", "children", "imgProps", "includePadding", "showBorder", "size", "imgFit", "src", "fallbackIconOverride", "type", "variant", "email", "initials", "placement"]);
    var ctx = useAvatarContext();
    var classes = useMergedClasses(avatarClasses, getAvatarUtilityClass, props.classes);
    var size = sizeProp || ctx.size;
    var type = typeProp || ctx.type || 'resource';
    var _c = useAvatarImage({ src: srcProp, type: type, email: email }), src = _c.src, status = _c.status;
    var variantValue = variantProp || ctx.variant;
    var FallbackIcon = fallbackIconOverride || fallbackIcon[type];
    var variantForType = getDefaultVariantForType(type);
    var variant = variantValue != null ? variantValue : variantForType;
    var children = null;
    var hasImgNotFailing = !!src && status !== 'error';
    var ownerState = {
        type: type,
        showBorder: showBorder,
        includePadding: includePadding,
        hasImgNotFailing: hasImgNotFailing,
        placement: placement,
        variant: variant,
        size: size,
        color: color,
    };
    var ownerStateImg = {
        imgFit: imgFit,
    };
    if (hasImgNotFailing) {
        children = React.createElement(Img, __assign({ alt: alt || '', src: src, ownerState: ownerStateImg, className: classes.img }, imgProps));
    }
    else if (childrenProp != null) {
        children = childrenProp;
    }
    else if ((type === 'user' || type === 'logo') && initials) {
        children = initials.substring(0, 2);
    }
    else {
        children = React.createElement(FallbackIcon, { className: classes.fallbackIcon });
    }
    return (React.createElement(Root, __assign({}, { ref: ref }, { ownerState: ownerState, className: clsx(classes.root, className) }, rootProps), children));
});
